"use client"

import { Button } from "@/components/ui/button"
import { downloadFileBlob } from "@/lib/api/export"
import { useState } from "react"
import { toast } from "sonner"

interface ExportButtonProps {
  /**
   * The URL to download from
   */
  url: string

  /**
   * The filename to save as
   */
  fileName: string

  /**
   * Optional text to display on the button
   */
  buttonText?: string

  /**
   * Optional className to apply to the button
   */
  className?: string
}

/**
 * A button component for exporting data
 * Shows loading state and handles errors with toast messages
 */
export default function ExportButton({
  url,
  fileName,
  buttonText = "Export",
  className,
}: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    if (isExporting) return

    setIsExporting(true)

    try {
      // Use toast.promise to show loading state and success/error messages
      await toast.promise(downloadFileBlob(url, fileName), {
        loading: `Preparing ${fileName} for download...`,
        success: `${fileName} export ready`,
        error: (err) => {
          if (err instanceof Error) {
            return err.message
          }
          return `Failed to export ${fileName}`
        },
      })
    } catch (error) {
      // Error is already handled by toast.promise
      console.error(`Error exporting ${fileName}:`, error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={isExporting}
      className={`w-auto ${className || ""}`}
    >
      {isExporting ? "Exporting..." : buttonText}
    </Button>
  )
}
